import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowUpRight, Compass, Radar, Search, Target, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import SellerIdentity from "@/components/SellerIdentity";
import { BUSINESS, businessAddress } from "@/lib/business";

const VALUES = [
  {
    icon: Search,
    title: "Evidence before opinion",
    body:
      "Every trend on Trends.Discovery comes from signals we can point at — search movement, marketplace demand and social velocity — not from a hunch or a guru's newsletter.",
  },
  {
    icon: Target,
    title: "One honest price",
    body:
      "A single $39 payment, for life. No subscription, no tiers, no upsell funnel. If it isn't useful within 14 days, we refund it.",
  },
  {
    icon: Compass,
    title: "Useful, not overwhelming",
    body:
      "A trend is only worth anything if you can act on it. Each blueprint tells you who to sell to, at what price, and what to do in the first seven days.",
  },
];

export default function About() {
  const address = businessAddress();

  return (
    <>
      <Helmet>
        <title>About Skaiboss — the team behind Trends.Discovery</title>
        <meta
          name="description"
          content="Skaiboss builds Trends.Discovery, a trend-scanning tool that turns early demand signals into launch-ready blueprints. Read our story, mission and who we are."
        />
        <link rel="canonical" href="https://trendsdiscovery.com/about" />
        <meta property="og:title" content="About Skaiboss — the team behind Trends.Discovery" />
        <meta
          property="og:description"
          content="Our story, mission and team behind Trends.Discovery — early demand signals turned into launch-ready blueprints."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: BUSINESS.legalName,
            alternateName: BUSINESS.tradingName,
            url: "https://trendsdiscovery.com",
            email: BUSINESS.email,
            address: {
              "@type": "PostalAddress",
              streetAddress: BUSINESS.addressLines[0],
              postalCode: "2552 PH",
              addressLocality: "The Hague",
              addressCountry: "NL",
            },
          })}
        </script>
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <div className="flex items-center justify-between gap-4 mb-10">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="size-4" /> Back to home
            </Link>
            <span className="inline-flex items-center gap-2 font-mono text-xs text-primary">
              <Radar className="size-4" /> TRENDS DISCOVERY
            </span>
          </div>

          <div className="font-mono text-xs text-primary mb-3">// ABOUT SKAIBOSS</div>
          <h1 className="font-display text-3xl md:text-5xl font-bold leading-tight">
            We built the tool we kept wishing existed.
          </h1>
          <p className="mt-5 text-lg text-muted-foreground">
            Skaiboss is a small independent software studio in The Hague. Trends.Discovery is our
            answer to a problem we ran into over and over: by the time a product trend is obvious,
            the easy money is gone.
          </p>

          <section className="mt-12 rounded-2xl border border-border bg-card p-6 md:p-8">
            <h2 className="font-display text-2xl font-bold">Our story</h2>
            <div className="mt-4 space-y-4 text-muted-foreground leading-relaxed">
              <p>
                We spent years launching small products online — accessories, digital tools, niche
                gear. The pattern was always the same. We would find a promising product, spend
                weeks preparing a launch, and arrive just as the market saturated. The winners
                weren't the people with the best execution. They were the people who arrived early.
              </p>
              <p>
                So we stopped guessing and started measuring. We wired up scanners across search
                data, marketplace listings and social platforms, scored what was accelerating rather
                than what was already big, and wrote ourselves a short brief for each idea worth
                acting on. That internal habit became Trends.Discovery.
              </p>
              <p>
                Today the scanners run nightly, the scoring is automated, and every trend that
                passes review becomes a blueprint you can read in five minutes and act on the same
                afternoon. Three of those blueprints are free to read, so you can judge the quality
                before paying anything.
              </p>
            </div>
          </section>

          <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
            <h2 className="font-display text-2xl font-bold">Our mission</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Give independent sellers and small teams the same early-signal advantage that large
              retailers buy from expensive market-research firms — for the price of a single lunch,
              paid once.
            </p>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {VALUES.map((v) => (
                <div key={v.title} className="rounded-xl border border-border bg-background/50 p-5">
                  <v.icon className="size-5 text-primary" />
                  <h3 className="mt-3 font-display text-lg font-semibold">{v.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.body}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
            <h2 className="font-display text-2xl font-bold flex items-center gap-2">
              <Users className="size-5 text-primary" /> The team
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Trends.Discovery is built and run by the Skaiboss founding team — a two-person studio
              handling the scanning pipeline, the product and customer support directly. There is no
              call centre and no outsourced inbox: when you write to{" "}
              <a href={`mailto:${BUSINESS.email}`} className="text-primary hover:underline">
                {BUSINESS.email}
              </a>
              , one of us answers, usually within a day.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              Want your name, role and photo on this page? Send them over and we'll add the full
              team profiles here.
            </p>
          </section>

          <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
            <h2 className="font-display text-2xl font-bold">Company details</h2>
            <dl className="mt-4 grid gap-4 sm:grid-cols-2 text-sm">
              <div>
                <dt className="font-mono text-xs text-muted-foreground">LEGAL NAME</dt>
                <dd className="mt-1">{BUSINESS.legalName}</dd>
              </div>
              <div>
                <dt className="font-mono text-xs text-muted-foreground">TRADING AS</dt>
                <dd className="mt-1">{BUSINESS.tradingName}</dd>
              </div>
              {address && (
                <div>
                  <dt className="font-mono text-xs text-muted-foreground">REGISTERED ADDRESS</dt>
                  <dd className="mt-1">{address}</dd>
                </div>
              )}
              <div>
                <dt className="font-mono text-xs text-muted-foreground">CONTACT</dt>
                <dd className="mt-1">
                  <a href={`mailto:${BUSINESS.email}`} className="text-primary hover:underline">
                    {BUSINESS.email}
                  </a>
                </dd>
              </div>
            </dl>
          </section>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button variant="hero" asChild>
              <Link to="/marketplace">
                Browse the blueprints <ArrowUpRight className="size-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/support">Contact support</Link>
            </Button>
          </div>

          <SellerIdentity className="mt-10" />
        </div>
      </main>
    </>
  );
}
